import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./PublicAgentAvailability.module.css";
import Modal2 from "../Modal2/Modal2";
import HeaderBar from "../HeaderBar/HeaderBar";
import {
  getAgentAvailability,
  updateAgentAvailability,
  getBusinessHoursByBusinessId,
} from "../../Store/apiStore";

/* ------------------------------- constants ------------------------------- */
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Monday → Sunday
const DURATION_OPTIONS = [15, 30, 45, 60];
const BUFFER_OPTIONS = [0, 5, 10, 15, 30, 60];
const HORIZON_OPTIONS = [14, 30, 60];

// App operates in the US & Canada only — show just those timezones.
const TZ_OPTIONS = [
  // United States
  { v: "America/New_York", l: "Eastern Time (US)" },
  { v: "America/Chicago", l: "Central Time (US)" },
  { v: "America/Denver", l: "Mountain Time (US)" },
  { v: "America/Phoenix", l: "Arizona (US, no DST)" },
  { v: "America/Los_Angeles", l: "Pacific Time (US)" },
  { v: "America/Anchorage", l: "Alaska Time (US)" },
  { v: "Pacific/Honolulu", l: "Hawaii Time (US)" },
  // Canada
  { v: "America/Toronto", l: "Eastern Time (Canada)" },
  { v: "America/Winnipeg", l: "Central Time (Canada)" },
  { v: "America/Edmonton", l: "Mountain Time (Canada)" },
  { v: "America/Vancouver", l: "Pacific Time (Canada)" },
  { v: "America/Halifax", l: "Atlantic Time (Canada)" },
  { v: "America/St_Johns", l: "Newfoundland Time (Canada)" },
];

const TIME_OPTIONS = (() => {
  const out = [];
  for (let m = 0; m < 24 * 60; m += 30) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  }
  return out;
})();

const fmt12 = (hm) => {
  let [h, m] = String(hm || "00:00").split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};
const tzLabel = (v) => TZ_OPTIONS.find((t) => t.v === v)?.l || v;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" });
};

function defaultWeekly() {
  return [
    { enabled: false, start: "09:00", end: "17:00" },
    { enabled: true, start: "09:00", end: "17:00" },
    { enabled: true, start: "09:00", end: "17:00" },
    { enabled: true, start: "09:00", end: "17:00" },
    { enabled: true, start: "09:00", end: "17:00" },
    { enabled: true, start: "09:00", end: "17:00" },
    { enabled: true, start: "10:00", end: "14:00" },
  ];
}

/* --------------------------------- icons ---------------------------------- */
const ICON_COLOR = "#6524EB";
const svgProps = (size) => ({
  width: size, height: size, viewBox: "0 0 24 24", fill: "none",
  stroke: ICON_COLOR, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round",
  style: { flex: "0 0 auto" },
});
const IconGlobe = ({ size = 18 }) => (
  <svg {...svgProps(size)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const IconClock = ({ size = 18 }) => (
  <svg {...svgProps(size)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconHourglass = ({ size = 18 }) => (
  <svg {...svgProps(size)}>
    <path d="M6 2h12" />
    <path d="M6 22h12" />
    <path d="M6 2c0 6 6 6 6 10s-6 4-6 10" />
    <path d="M18 2c0 6-6 6-6 10s6 4 6 10" />
  </svg>
);
const IconBell = ({ size = 18 }) => (
  <svg {...svgProps(size)}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const IconCalendar = ({ size = 18 }) => (
  <svg {...svgProps(size)}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconEdit = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);
const IconTrash = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const IconInfo = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const IconAlertTriangle = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/* ------------------------------ small UI bits ----------------------------- */
const Toggle = ({ on, onClick, disabled }) => (
  <button type="button" className={`${styles.switch} ${on ? styles.switchOn : ""}`} onClick={onClick} disabled={disabled} aria-pressed={on}>
    <span className={styles.knob} />
  </button>
);

const TimeSelect = ({ value, onChange }) => (
  <select className={styles.timeSelect} value={value} onChange={(e) => onChange(e.target.value)}>
    {TIME_OPTIONS.map((t) => (
      <option key={t} value={t}>{fmt12(t)}</option>
    ))}
  </select>
);

const Row = ({ icon, label, value, onClick }) => (
  <button type="button" className={styles.summaryItem} onClick={onClick} disabled={!onClick}>
    <span className={styles.sumIcon}>{icon}</span>
    <span className={styles.sumText}>
      <span className={styles.sumLabel}>{label}</span>
      <span className={styles.sumValue}>{value}</span>
    </span>
    {onClick && <span className={styles.chev}>›</span>}
  </button>
);

/* ------------------------------- component -------------------------------- */
export default function PublicAgentAvailability() {
  const navigate = useNavigate();
  const agentId = sessionStorage.getItem("SelectAgentId") || "";

  const [view, setView] = useState("overview"); // overview | weekly | overrides | rules
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // override index pending delete

  // Business Hours (Agent Schedule) me jo din "Closed" hain — unke weekday index.
  // Inhi se hum warn karte hain jab owner usi din booking availability ON kare.
  const [bizClosedDays, setBizClosedDays] = useState(() => new Set());
  const [warnDay, setWarnDay] = useState(null); // weekday index, confirm pending

  const [cfg, setCfg] = useState({
    businessName: "",
    agentName: "",
    avatar: null,
    timezone: "America/New_York",
    durationMins: 30,
    bufferMins: 15,
    minNoticeMins: 120,
    horizonDays: 30,
    useVisitorLocalTime: true,
    weekly: defaultWeekly(),
    overrides: [],
  });

  // Override editor
  const [ovDate, setOvDate] = useState(todayIso());
  const [ovAllDay, setOvAllDay] = useState(false);
  const [ovStart, setOvStart] = useState("12:00");
  const [ovEnd, setOvEnd] = useState("16:00");
  const [editingIdx, setEditingIdx] = useState(null);

  // Minimum-notice editor (value + unit)
  const noticeUnit = cfg.minNoticeMins % 1440 === 0 && cfg.minNoticeMins >= 1440 ? "days" : "hours";
  const noticeValue = noticeUnit === "days" ? cfg.minNoticeMins / 1440 : Math.max(0, Math.round(cfg.minNoticeMins / 60));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAgentAvailability(agentId);
        if (active && res?.success && res.data) {
          setCfg((p) => ({ ...p, ...res.data, weekly: res.data.weekly || defaultWeekly(), overrides: res.data.overrides || [] }));

          const bId =
            res.data.businessId ||
            sessionStorage.getItem("SelectAgentBusinessId") ||
            (() => {
              try {
                const details = JSON.parse(sessionStorage.getItem("agentDetails") || "{}");
                return details?.agent?.businessId || details?.businessId || "";
              } catch {
                return "";
              }
            })();

          if (bId) {
            try {
              const bizRes = await getBusinessHoursByBusinessId(bId);
              const hoursRaw = bizRes?.hoursRaw || bizRes?.hours || (typeof bizRes === "string" ? bizRes : "");
              if (hoursRaw) {
                const closedSet = new Set();
                const blocks = hoursRaw.split("|").map((s) => s.trim()).filter(Boolean);
                blocks.forEach((block) => {
                  const idx = block.indexOf(":");
                  if (idx >= 0) {
                    const dayName = block.slice(0, idx).trim();
                    const timePart = block.slice(idx + 1).trim();
                    if (/closed/i.test(timePart)) {
                      const dayIdx = DAY_NAMES.findIndex((d) => d.toLowerCase() === dayName.toLowerCase());
                      if (dayIdx >= 0) closedSet.add(dayIdx);
                    }
                  }
                });
                if (active) setBizClosedDays(closedSet);
              }
            } catch (err) {
              console.error("load business hours failed", err);
            }
          }
        }
      } catch (e) {
        console.error("load availability failed", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [agentId]);


  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const res = await updateAgentAvailability({ agent_id: agentId, ...next });
      if (res?.success && res.data) setCfg((p) => ({ ...p, ...res.data }));
      toast.success("Saved");
    } catch (e) {
      console.error("save availability failed", e);
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const setWeeklyDay = (idx, patch) => {
    const weekly = cfg.weekly.map((d, i) => (i === idx ? { ...d, ...patch } : d));
    setCfg((p) => ({ ...p, weekly }));
  };

  const handleToggleDay = (idx) => {
    const isCurrentlyEnabled = cfg.weekly[idx]?.enabled;
    if (!isCurrentlyEnabled && bizClosedDays.has(idx)) {
      setWarnDay(idx);
    } else {
      setWeeklyDay(idx, { enabled: !isCurrentlyEnabled });
    }
  };

  const confirmEnableWarnDay = () => {
    if (warnDay != null) {
      setWeeklyDay(warnDay, { enabled: true });
      setWarnDay(null);
    }
  };

  const resetOvForm = () => {
    setOvDate(todayIso()); setOvAllDay(false); setOvStart("12:00"); setOvEnd("16:00"); setEditingIdx(null);
  };
  const dedupeSort = (list) =>
    list
      .filter((x, i, arr) => arr.findIndex((y) => y.date === x.date && y.allDay === x.allDay && y.start === x.start && y.end === x.end) === i)
      .sort((a, b) => a.date.localeCompare(b.date));

  // Add or update an override AND persist immediately, so the owner sees it.
  const addOrUpdateOverride = () => {
    if (!ovDate) return;
    const o = { date: ovDate, allDay: ovAllDay, start: ovStart, end: ovEnd };
    let list;
    if (editingIdx != null) {
      list = cfg.overrides.map((x, i) => (i === editingIdx ? o : x));
    } else {
      list = [...cfg.overrides, o];
    }
    list = dedupeSort(list);
    resetOvForm();
    save({ overrides: list });
  };
  const startEditOverride = (i) => {
    const o = cfg.overrides[i];
    if (!o) return;
    setOvDate(o.date); setOvAllDay(!!o.allDay); setOvStart(o.start || "12:00"); setOvEnd(o.end || "16:00");
    setEditingIdx(i);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { }
  };
  const deleteOverride = (i) => {
    const list = cfg.overrides.filter((_, idx) => idx !== i);
    if (editingIdx === i) resetOvForm();
    save({ overrides: list });
  };
  // From the overview card: jump to the overrides screen with this one loaded.
  const editFromOverview = (i) => {
    setView("overrides");
    startEditOverride(i);
  };

  const weeklySummary = useMemo(() => {
    // Group consecutive (display-order) days with identical enabled hours.
    const items = [];
    DISPLAY_ORDER.forEach((di) => {
      const d = cfg.weekly[di];
      const label = d.enabled ? `${fmt12(d.start)} – ${fmt12(d.end)}` : "Off";
      items.push({ day: DAY_NAMES[di], short: DAY_SHORT[di], label });
    });
    return items;
  }, [cfg.weekly]);

  // View navigation.
  const go = (v) => {
    if (v === "overrides") resetOvForm();
    setView(v);
  };

  const back = () => {
    if (view !== "overview") setView("overview");
    else navigate(-1);
  };

  const headerTitle = {
    overview: "Availability & Scheduling",
    weekly: "Weekly Availability",
    overrides: "Date-Specific Overrides",
    rules: "Booking Rules",
  }[view];

  /* --------------------------------- render -------------------------------- */
  return (
    <div className={styles.page}>
      {/* Shared HeaderBar — back apna (sub-view → overview, warna pichhla page). */}
      <div className={styles.header}>
        <HeaderBar title={headerTitle} onBack={back} />
      </div>

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <>
          {view === "overview" && (
            <Overview
              cfg={cfg}
              noticeValue={noticeValue}
              noticeUnit={noticeUnit}
              weeklySummary={weeklySummary}
              go={go}
              onEditOverride={editFromOverview}
              onDeleteOverride={(i) => setConfirmDel(i)}
            />
          )}

          {view === "weekly" && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Set your weekly availability</div>
              {DISPLAY_ORDER.map((di) => {
                const d = cfg.weekly[di];
                const isBizClosed = bizClosedDays.has(di);
                return (
                  <div key={di} className={styles.dayRow}>
                    <Toggle on={d.enabled} onClick={() => handleToggleDay(di)} />
                    <span className={styles.dayName}>
                      {DAY_NAMES[di]}
                      {d.enabled && isBizClosed && (
                        <span className={styles.warnTag} title="This day is marked as Closed in your Business Hours">⚠️ Closed in Business Hours</span>
                      )}
                    </span>
                    {d.enabled ? (
                      <span className={styles.dayTimes}>
                        <TimeSelect value={d.start} onChange={(v) => setWeeklyDay(di, { start: v })} />
                        <span className={styles.toSep}>to</span>
                        <TimeSelect value={d.end} onChange={(v) => setWeeklyDay(di, { end: v })} />
                      </span>
                    ) : (
                      <span className={styles.offText}>Off</span>
                    )}
                  </div>
                );
              })}

              <div className={styles.subBlock}>
                <div className={styles.subLabel}>Default Appointment Duration</div>
                <div className={styles.pillRow}>
                  {DURATION_OPTIONS.map((m) => (
                    <button key={m} type="button" className={`${styles.pill} ${cfg.durationMins === m ? styles.pillOn : ""}`} onClick={() => setCfg((p) => ({ ...p, durationMins: m }))}>
                      {m} min
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.note}><IconInfo size={14} /><span>By default, these hours follow your business hours.</span></div>
              <button className={styles.saveBtn} disabled={saving} onClick={() => save({ weekly: cfg.weekly, durationMins: cfg.durationMins })}>
                {saving ? "Saving…" : "Save Weekly Availability"}
              </button>
            </div>
          )}

          {view === "overrides" && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{editingIdx != null ? "Edit Override" : "Add Override"}</div>
              <label className={styles.fieldLabel}>Date</label>
              <input type="date" className={styles.dateInput} value={ovDate} min={todayIso()} onChange={(e) => setOvDate(e.target.value)} />

              <div className={styles.toggleRow}>
                <span>Block entire day</span>
                <Toggle on={ovAllDay} onClick={() => setOvAllDay((v) => !v)} />
              </div>

              {!ovAllDay && (
                <div className={styles.customTime}>
                  <label className={styles.fieldLabel}>Custom time (blocked)</label>
                  <div className={styles.timeRange}>
                    <TimeSelect value={ovStart} onChange={setOvStart} />
                    <span className={styles.toSep}>to</span>
                    <TimeSelect value={ovEnd} onChange={setOvEnd} />
                  </div>
                </div>
              )}
              <div className={styles.ovFormBtns}>
                <button className={styles.addBtn} disabled={saving} onClick={addOrUpdateOverride}>
                  {saving ? "Saving…" : editingIdx != null ? "Update Override" : "+ Add Override"}
                </button>
                {editingIdx != null && (
                  <button className={styles.cancelBtn} onClick={resetOvForm}>Cancel</button>
                )}
              </div>

              <div className={styles.sectionTitle} style={{ marginTop: 20 }}>Upcoming Overrides</div>
              {cfg.overrides.filter((o) => o.date >= todayIso()).length === 0 ? (
                <div className={styles.empty}>No overrides yet. Add one above — it saves automatically.</div>
              ) : (
                cfg.overrides
                  .map((o, i) => ({ o, i }))
                  .filter(({ o }) => o.date >= todayIso())
                  .map(({ o, i }) => (
                    <div key={i} className={`${styles.ovItem} ${editingIdx === i ? styles.ovEditing : ""}`}>
                      <span className={styles.ovIcon}><IconCalendar size={18} /></span>
                      <span className={styles.ovText}>
                        <span className={styles.ovDate}>{prettyDate(o.date)}</span>
                        <span className={styles.ovSub}>{o.allDay ? "Closed all day" : `Blocked ${fmt12(o.start)} – ${fmt12(o.end)}`}</span>
                      </span>
                      <button className={styles.ovEdit} onClick={() => startEditOverride(i)} aria-label="Edit" title="Edit"><IconEdit size={16} /></button>
                      <button className={styles.ovDel} onClick={() => setConfirmDel(i)} aria-label="Delete" title="Delete"><IconTrash size={16} /></button>
                    </div>
                  ))
              )}
              <div className={styles.note}><IconInfo size={14} /><span>Overrides save automatically when you add, update or delete them.</span></div>
            </div>
          )}

          {view === "rules" && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Configure your booking rules</div>

              <div className={styles.ruleRow}>
                <div className={styles.ruleText}>
                  <div className={styles.ruleLabel}>Buffer Intervals</div>
                  <div className={styles.ruleSub}>Time before & after each booking.</div>
                </div>
                <select className={styles.ruleSelect} value={cfg.bufferMins} onChange={(e) => setCfg((p) => ({ ...p, bufferMins: Number(e.target.value) }))}>
                  {BUFFER_OPTIONS.map((m) => <option key={m} value={m}>{m} min</option>)}
                </select>
              </div>

              <div className={styles.ruleRow}>
                <div className={styles.ruleText}>
                  <div className={styles.ruleLabel}>Minimum Notice</div>
                  <div className={styles.ruleSub}>Minimum time before a booking can be made.</div>
                </div>
                <div className={styles.noticeCtl}>
                  <input type="number" min="0" className={styles.noticeInput} value={noticeValue}
                    onChange={(e) => {
                      const val = Math.max(0, Number(e.target.value) || 0);
                      setCfg((p) => ({ ...p, minNoticeMins: noticeUnit === "days" ? val * 1440 : val * 60 }));
                    }} />
                  <div className={styles.segment}>
                    {["hours", "days"].map((u) => (
                      <button key={u} type="button" className={`${styles.seg} ${noticeUnit === u ? styles.segOn : ""}`}
                        onClick={() => setCfg((p) => ({ ...p, minNoticeMins: u === "days" ? noticeValue * 1440 : noticeValue * 60 }))}>
                        {u === "hours" ? "Hours" : "Days"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.ruleRow}>
                <div className={styles.ruleText}>
                  <div className={styles.ruleLabel}>Maximum Scheduling Horizon</div>
                  <div className={styles.ruleSub}>How far in advance customers can book.</div>
                </div>
                <div className={styles.segment}>
                  {HORIZON_OPTIONS.map((d) => (
                    <button key={d} type="button" className={`${styles.seg} ${cfg.horizonDays === d ? styles.segOn : ""}`} onClick={() => setCfg((p) => ({ ...p, horizonDays: d }))}>
                      {d} Days
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.note}><IconInfo size={14} /><span>Appointment times always follow the business timezone.</span></div>
              <button className={styles.saveBtn} disabled={saving} onClick={() => save({
                bufferMins: cfg.bufferMins, minNoticeMins: cfg.minNoticeMins, horizonDays: cfg.horizonDays,
                useVisitorLocalTime: cfg.useVisitorLocalTime,
              })}>
                {saving ? "Saving…" : "Save Booking Rules"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Warning Modal for Business Hours Conflict */}
      <Modal2 isOpen={warnDay != null} onClose={() => setWarnDay(null)}>
        <div className={styles.confirmBox}>
          <div className={styles.warnIcon}><IconAlertTriangle size={26} /></div>
          <div className={styles.confirmTitle}>Business Hours Warning</div>
          <p className={styles.confirmMsg}>
            {warnDay != null
              ? `${DAY_NAMES[warnDay]} is currently marked as Closed in your Business Hours (Agent Schedule). Enabling booking availability will allow customers to book appointments on this day even though your business hours show as closed.`
              : "This day is marked as Closed in your Business Hours."}
          </p>
          <div className={styles.confirmBtns}>
            <button className={styles.confirmKeep} onClick={() => setWarnDay(null)}>Keep Closed</button>
            <button className={styles.warnEnableBtn} onClick={confirmEnableWarnDay}>Enable Anyway</button>
          </div>
        </div>
      </Modal2>

      <Modal2 isOpen={confirmDel != null} onClose={() => setConfirmDel(null)} isLoading={saving}>
        <div className={styles.confirmBox}>
          <div className={styles.confirmIcon}><IconTrash size={26} /></div>
          <div className={styles.confirmTitle}>Delete override?</div>
          <p className={styles.confirmMsg}>
            {confirmDel != null && cfg.overrides[confirmDel]
              ? `This will remove the override for ${prettyDate(cfg.overrides[confirmDel].date)}. Bookings will follow your normal availability again.`
              : "This override will be removed."}
          </p>
          <div className={styles.confirmBtns}>
            <button className={styles.confirmKeep} onClick={() => setConfirmDel(null)}>Keep</button>
            <button
              className={styles.confirmDelete}
              onClick={() => {
                const i = confirmDel;
                setConfirmDel(null);
                deleteOverride(i);
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal2>
    </div>
  );
}

/* --------------------------------- overview ------------------------------- */
function Overview({ cfg, noticeValue, noticeUnit, weeklySummary, go, onEditOverride, onDeleteOverride }) {
  const today = todayIso();
  const upcomingOverrides = (cfg.overrides || [])
    .map((o, i) => ({ o, i }))
    .filter(({ o }) => o.date >= today)
    .sort((a, b) => a.o.date.localeCompare(b.o.date));
  return (
    <>
      <div className={styles.section}>
        <div className={styles.bizNameBig}>{cfg.businessName || cfg.agentName || "Your Business"}</div>
        <div className={styles.summaryGrid}>
          <Row icon={<IconClock />} label="Appointment Duration" value={`${cfg.durationMins} min`} />
          <Row icon={<IconHourglass />} label="Buffer Time" value={`${cfg.bufferMins} min`} />
          <Row icon={<IconBell />} label="Min Notice" value={`${noticeValue} ${noticeUnit}`} />
          <Row icon={<IconCalendar />} label="Booking Horizon" value={`${cfg.horizonDays} days`} />
        </div>
      </div>

      <div className={styles.blockTitle}>Weekly Availability</div>
      <div className={styles.section}>
        {weeklySummary.map((w) => (
          <div key={w.day} className={styles.summaryLine}>
            <span className={styles.slDay}>{w.day}</span>
            <span className={`${styles.slTime} ${w.label === "Off" ? styles.slOff : ""}`}>{w.label}</span>
          </div>
        ))}
        <button className={styles.linkBtn} onClick={() => go("weekly")}><IconEdit size={14} /><span>Edit Weekly Availability</span><span className={styles.chev}>›</span></button>
      </div>

      <div className={styles.blockTitle}>Date-Specific Overrides</div>
      <div className={styles.section}>
        {upcomingOverrides.length === 0 ? (
          <div className={styles.muted}>Add special hours for holidays, events, or other date-specific changes.</div>
        ) : (
          upcomingOverrides.slice(0, 2).map(({ o, i }) => (
            <div key={i} className={styles.ovItem}>
              <span className={styles.ovIcon}><IconCalendar size={18} /></span>
              <span className={styles.ovText}>
                <span className={styles.ovDate}>{prettyDate(o.date)}</span>
                <span className={styles.ovSub}>{o.allDay ? "Closed all day" : `Blocked ${fmt12(o.start)} – ${fmt12(o.end)}`}</span>
              </span>
              <button className={styles.ovEdit} onClick={() => onEditOverride(i)} aria-label="Edit" title="Edit"><IconEdit size={16} /></button>
              <button className={styles.ovDel} onClick={() => onDeleteOverride(i)} aria-label="Delete" title="Delete"><IconTrash size={16} /></button>
            </div>
          ))
        )}
        {upcomingOverrides.length > 2 && (
          <div className={styles.muted} style={{ marginTop: 6 }}>+{upcomingOverrides.length - 2} more</div>
        )}
        <button className={styles.linkBtn} onClick={() => go("overrides")}>Manage Overrides ›</button>
      </div>

      <div className={styles.blockTitle}>Booking Rules</div>
      <div className={styles.section}>
        <Row icon={<IconClock />} label="Buffer Intervals" value={`${cfg.bufferMins} min`} onClick={() => go("rules")} />
        <Row icon={<IconBell />} label="Minimum Notice" value={`${noticeValue} ${noticeUnit}`} onClick={() => go("rules")} />
        <Row icon={<IconCalendar />} label="Maximum Scheduling Horizon" value={`${cfg.horizonDays} days`} onClick={() => go("rules")} />
      </div>
    </>
  );
}
